import type {
  QueryClient,
  QueryKey,
  QueryObserverOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import { useLocation } from '@tanstack/react-router'
import type { RequestConfig, ResponseErrorConfig } from './v1/.kubb/client'
import { isUserAdmin } from '@/types/Role'
import {
  type ListOrderBooksStatus400,
  type ListOrderBooksStatus404,
  type ListOrderBooksStatus500,
  type ListOrderBooksQuery,
  type ListOrderBooksStatus200,
  orderBookStatus,
  useGetUserSelf,
  useListOrderBooks,
} from './v1'
import { listOrderBooksQueryKey } from './v1/hooks/useListOrderBooks'

type ListOrderBooksQueryKey = ReturnType<typeof listOrderBooksQueryKey>

type ListOpenOrderBooksQuery = Omit<ListOrderBooksQuery, 'status'>
type ListOrderBookStatuses = NonNullable<ListOrderBooksQuery['status']>

const openOrderBookStatuses: ListOrderBookStatuses = [
  orderBookStatus.OPEN,
  orderBookStatus.SUSPENDED,
]
const adminOrderBookStatuses: ListOrderBookStatuses = Object.values(orderBookStatus)

export function useListOpenOrderBooks<
  TData = ListOrderBooksStatus200,
  TQueryData = ListOrderBooksStatus200,
  TQueryKey extends QueryKey = ListOrderBooksQueryKey,
>(
  params?: ListOpenOrderBooksQuery,
  options: {
    includeAdminStatuses?: boolean
    query?: Partial<
      QueryObserverOptions<
        ListOrderBooksStatus200,
        ResponseErrorConfig<
          ListOrderBooksStatus400 | ListOrderBooksStatus404 | ListOrderBooksStatus500
        >,
        TData,
        TQueryData,
        TQueryKey
      >
    > & { client?: QueryClient }
    client?: Partial<Omit<RequestConfig, 'path' | 'query' | 'body' | 'headers' | 'url'>>
  } = {},
) {
  const location = useLocation()
  const includeAdminStatuses = options.includeAdminStatuses ?? location.pathname !== '/viewOnlyMode'
  const {
    data: userSelfResponse,
    isError: isUserSelfError,
    isSuccess: isUserSelfSuccess,
  } = useGetUserSelf({
    query: {
      enabled: includeAdminStatuses,
    },
  })
  const isAdmin = includeAdminStatuses ? (isUserAdmin(userSelfResponse?.data) ?? false) : false
  const isAdminStatusResolved =
    !includeAdminStatuses || isUserSelfSuccess || isUserSelfError || Boolean(userSelfResponse)

  const resolvedParams: ListOrderBooksQuery = {
    ...params,
    status: isAdmin ? adminOrderBookStatuses : openOrderBookStatuses,
  }

  const {
    includeAdminStatuses: _includeAdminStatuses,
    query: queryConfig = {},
    ...restOptions
  } = options
  const { enabled, ...queryOptionsConfig } = queryConfig
  const queryEnabled =
    typeof enabled === 'function'
      ? (query: unknown) => isAdminStatusResolved && (enabled as (query: unknown) => boolean)(query)
      : (enabled ?? true) && isAdminStatusResolved

  return useListOrderBooks<TData, TQueryData, TQueryKey>(
    { query: resolvedParams },
    {
      ...restOptions,
      query: {
        ...queryOptionsConfig,
        enabled: queryEnabled,
      },
    },
  ) as UseQueryResult<
    TData,
    ResponseErrorConfig<ListOrderBooksStatus400 | ListOrderBooksStatus404 | ListOrderBooksStatus500>
  > & { queryKey: TQueryKey }
}
